import { useEffect, useRef, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import TimezoneSelect, { ITimezone } from 'react-timezone-select';
import { usePrevious } from 'react-delta';
import { useMutation } from '@apollo/client';
import { useRecoilState, useRecoilValue } from 'recoil';
import isString from 'lodash/isString';
import flatten from 'lodash/flatten';

import { accountDataState, activeTeamState } from 'components/account/account.state';
import * as K from 'common/constants';
import { Scope, Theme } from 'common/graphqlTypes';
import { AvailableTheme } from 'common/types';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import ComboBox from 'components/common/ComboBox/ComboBox';
import { ButtonRef } from 'components/common/Button/Button';
import Dialog from 'components/common/Dialog/Dialog';
import { OptionType } from 'components/common/ReactSelect/Select';
import { THEME_INFO } from 'styles/stylesProvider/themes';
import ELEMENT from 'common/element';
import { convertUrlToRelative } from 'common/util/location';
import { processServerSettings } from 'common/util/settings';
import { savedSettingsState, settingsState, themeState } from 'components/common/Managers/SettingsManager.state';
import {
  homepageAvailableSectionsState,
  homepageIsValidState,
  homepagesExcludingCurrentState,
  preferencesState,
  timezoneChangeRequiresNewSearchState,
  userTeamHomepageState,
} from 'components/common/Managers/PreferencesManager.state';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { adjustTheme } from 'styles/stylesProvider/themes';

import { SET_PREFERENCES, SET_THEME } from './queries';

import './UserPreferencesDialog.scss';

type UserPreferencesDialogProps = {
  show: boolean;
  closeDialog: () => void;
};

function UserPreferencesDialog({ show, closeDialog }: UserPreferencesDialogProps): JSX.Element {
  const okButtonRef = useRef<ButtonRef>(null) as ButtonRef;
  const setSearchParams = useSetSearchParams();
  const timezoneChangeRequiresNewSearch = useRecoilValue(timezoneChangeRequiresNewSearchState);
  const activeTeam = useRecoilValue(activeTeamState);
  const theme = useRecoilValue(themeState);
  const [settings, setSettings] = useRecoilState(settingsState);
  const [savedSettings, setSavedSettings] = useRecoilState(savedSettingsState);
  const [preferences, setPreferences] = useRecoilState(preferencesState);
  const homepage = useRecoilValue(userTeamHomepageState);
  const homepageAvailableSections = useRecoilValue(homepageAvailableSectionsState);
  const homepagesExcludingCurrent = useRecoilValue(homepagesExcludingCurrentState);
  const [isUsingBrowserTimezone, setIsUsingBrowserTimezone] = useState(preferences?.timezone === null);
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezone>(
    preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const isUserHomepageValid = useRecoilValue(homepageIsValidState);
  const [homepageName, setHomepageName] = useState('');
  const [homepagePath, setHomepagePath] = useState(homepage || '');
  const [errorMsg, setErrorMsg] = useState('');
  const account = useRecoilValue(accountDataState);

  const themeInfo = THEME_INFO[theme];
  const pairedTheme = themeInfo.pairedWith;
  const pairedThemeInfo = THEME_INFO[pairedTheme];
  const themeOptions = themeInfo.isDark
    ? [
        { value: pairedTheme, label: pairedThemeInfo.label },
        { value: theme, label: themeInfo.label },
      ]
    : [
        { value: theme, label: themeInfo.label },
        { value: pairedTheme, label: pairedThemeInfo.label },
      ];
  const [selectedTheme, setSelectedTheme] = useState<OptionType>(themeInfo.isDark ? themeOptions[1] : themeOptions[0]);

  const [updateTheme, { error: updateThemeError, loading: updateThemeLoading }] = useMutation(SET_THEME, {
    onCompleted: ({ setDisplaySettings }) => {
      setSettings(processServerSettings(settings, setDisplaySettings));
      setSavedSettings(processServerSettings(savedSettings, setDisplaySettings));
    },
  });

  const [updatePreferencesMutation, { error: updatePreferencesError, loading: updatePreferencesLoading }] = useMutation(
    SET_PREFERENCES,
    {
      onCompleted: ({ updatePreferences }) => {
        setPreferences(updatePreferences);
        // If the current start and end time require rerunning the search for the new timezone,
        // call setSearchParams with force = true
        if (timezoneChangeRequiresNewSearch) {
          setSearchParams({} as Record<K.URL_PARAM, string>, true);
        }
      },
      onError: (error) => {
        setErrorMsg(error?.message);
      },
    },
  );

  const prevMutationLoading = usePrevious(updateThemeLoading || updatePreferencesLoading);

  const homepageAvailableComboSections = homepageAvailableSections.map((sections) =>
    sections.map(({ displayText }) => displayText),
  );

  const getHomepageItemDisplayText = (homepagePath?: string | null) =>
    homepagePath
      ? flatten(homepageAvailableSections).find(({ value }) => value === homepagePath)?.displayText
      : undefined;

  const getHomepagePathFromList = (displayText: string) =>
    flatten(homepageAvailableSections).find((item) => item.displayText === displayText)?.value;

  useEffect(() => {
    // `isUserHomepageValid` can be `true`, `false` or undefined (when no homepage preference has been saved)
    // We only want to show this error message the first time we load this preferences modal to let the user know that
    // the homepage he already configured is not correct
    if (isUserHomepageValid === false) {
      setErrorMsg('The homepage is not valid, you will be redirected to the default homepage instead');
    }
  }, []);

  useEffect(() => {
    setSelectedTimezone(preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    setIsUsingBrowserTimezone(preferences?.timezone === null);
  }, [preferences.timezone]);

  useEffect(() => {
    setHomepageName(getHomepageItemDisplayText(homepage) || homepage || '');
    setHomepagePath(homepage || '');
  }, [homepage]);

  useEffect(() => {
    setSelectedTheme(themeInfo.isDark ? themeOptions[1] : themeOptions[0]);
  }, [theme]);

  useEffect(() => {
    const currentMutationLoading = updateThemeLoading || updatePreferencesLoading;

    if (prevMutationLoading === true && currentMutationLoading === false) {
      if (!(updatePreferencesError || updateThemeError)) {
        // Closing the dialog after the mutations for theme and / or preferences are successful
        closeDialog();
      }
    }
  }, [updatePreferencesError, updatePreferencesLoading, updateThemeError, updateThemeLoading]);

  const handleSubmit = () => {
    setErrorMsg('');
    const newTheme = adjustTheme(selectedTheme.value as Theme, account.isSingularityXDRUser ?? false);

    const shouldUpdateTheme = newTheme !== theme;
    if (shouldUpdateTheme) {
      updateTheme({ variables: { scope: Scope.global, settings: { theme: newTheme } } });
    }

    const shouldUnsetTimezone = isUsingBrowserTimezone && preferences.timezone !== null;
    const shouldUpdatePreferences =
      shouldUnsetTimezone ||
      (!isUsingBrowserTimezone && selectedTimezone !== preferences.timezone) ||
      homepagePath !== (homepage ?? '');

    if (shouldUpdatePreferences) {
      const newTimezone = shouldUnsetTimezone
        ? null
        : isString(selectedTimezone)
        ? selectedTimezone
        : selectedTimezone.value;
      const [newHomepage, err] = convertUrlToRelative(homepagePath);

      if (err) {
        setErrorMsg(err);
      } else {
        const homepages = [
          ...homepagesExcludingCurrent,
          ...(newHomepage && newHomepage?.length > 0
            ? [
                {
                  team: activeTeam.id,
                  path: newHomepage,
                },
              ]
            : []),
        ];
        updatePreferencesMutation({
          variables: { homepages, timezone: newTimezone },
        });
      }
    }

    if (!shouldUpdateTheme && !shouldUpdatePreferences) {
      // Closing the dialog when no changes were made and user clicked on OK button
      closeDialog();
    }
  };

  const handleCancel = () => {
    setErrorMsg('');
    // this ensures that when a user makes some selection but closes the dialog without saving then we reset the state
    setSelectedTheme(themeInfo.isDark ? themeOptions[1] : themeOptions[0]);
    setHomepageName(getHomepageItemDisplayText(homepage) || homepage || '');
    setHomepagePath(homepage || '');
    setIsUsingBrowserTimezone(preferences?.timezone === null);
    setSelectedTimezone(preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    closeDialog();
  };

  const toggleTheme = () => {
    const themeToUse = themeOptions.find((option) => option.label !== selectedTheme.label);

    if (themeToUse) {
      setSelectedTheme(themeToUse);
    }
  };

  const handleSetHomepage = (value: string) => {
    const path = getHomepagePathFromList(value);
    if (path) {
      setHomepageName(value || '');
      setHomepagePath(path);
    } else {
      if (!value) {
        setHomepageName('');
        setHomepagePath('');
        return;
      }

      // Selection not in the list, so it should be a URL (if not, BE will validate)
      setHomepageName(value);
      setHomepagePath(value);
    }
  };

  const setHomepageToCurrentPage = () => {
    const currentPage = window.location.href;
    setHomepageName(currentPage);
    setHomepagePath(currentPage);
  };

  const getId = (themeOption: OptionType) =>
    THEME_INFO[themeOption.value as AvailableTheme].isDark
      ? ELEMENT.userPreferencesDialogThemeSelectRadioDarkOption
      : ELEMENT.userPreferencesDialogThemeSelectRadioLightOption;

  return (
    <Dialog
      className="UserPreferencesDialog"
      title="Preferences"
      show={show}
      close={handleCancel}
      closeBeforeActions={false}
      cancelCallback={handleCancel}
      okCallback={handleSubmit}
      focusButtonRef={okButtonRef}
      testId={ELEMENT.userPreferencesDialog}
      scrollable={false}
      errorMsg={errorMsg}
    >
      <div className="UserPreferencesDialog__Body">
        <Form>
          <Form.Group as={Row} className="UserPreferencesDialog__ThemeControlsContainer">
            <Form.Label column sm={3}>
              Theme
            </Form.Label>
            <Col sm={9}>
              {themeOptions.map((themeOption) => (
                <CheckboxRadio
                  type="radio"
                  label={themeOption.label}
                  onChange={toggleTheme}
                  checked={themeOption.label === selectedTheme.label}
                  key={getId(themeOption)}
                  id={getId(themeOption)}
                  testId={getId(themeOption)}
                />
              ))}
            </Col>
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column sm={3}>
              Timezone
            </Form.Label>
            <Col sm={9} className="UserPreferencesDialog__TimezoneControlsContainer">
              <CheckboxRadio
                type="radio"
                label="Local time (browser default)"
                checked={isUsingBrowserTimezone}
                onChange={() => setIsUsingBrowserTimezone(true)}
              />
              <div className="UserPreferencesDialog__TimezoneSelector">
                <CheckboxRadio
                  type="radio"
                  checked={!isUsingBrowserTimezone}
                  onChange={() => setIsUsingBrowserTimezone(false)}
                />
                <TimezoneSelect
                  value={selectedTimezone}
                  labelStyle="abbrev"
                  isDisabled={isUsingBrowserTimezone}
                  onChange={setSelectedTimezone}
                  classNamePrefix="CustomSelect"
                  className="ReactSelect SelectDropdown"
                  id={ELEMENT.userPreferencesDialogTimezoneSelect}
                  inputId={ELEMENT.userPreferencesDialogTimezoneSelectInput}
                />
              </div>
            </Col>
          </Form.Group>
          <Form.Group as={Row}>
            <Form.Label column sm={3}>
              Home Page
            </Form.Label>
            <Col sm={9}>
              <ComboBox
                value={homepageName}
                onChange={handleSetHomepage}
                onEnter={handleSetHomepage}
                itemSuffixSeparator="||"
                sections={homepageAvailableComboSections}
                selectOnEnter
              />
              <a onClick={() => setHomepageToCurrentPage()}>Use current page</a>
            </Col>
          </Form.Group>
        </Form>
      </div>
    </Dialog>
  );
}

export default UserPreferencesDialog;
